import React from "react";
import OpenQuestionMarkerForm from "./OpenQuestionMarkerForm";
import { useTranslation } from "react-i18next";
import "../../style/global.css";
import QcmMarkerForm from "./QcmMarkerForm";
import { DEFAULT_EMPTY_TEXT } from "../../map/GPX/Resources";
import QcmImageMarkerForm from "./QcmImageMarkerForm";
import {
  computePoiMediaContentKey,
  qcmReferenceFor,
  queuePoiMediaUpload,
} from "../../../api/poiMediaUploads";
import { isImageChecksum } from "../../../domain/mainImage";
import DialogBox from "../../util/DialogBox";
import LoadingBox, { closeLoadingBox } from "../../util/LoadingBox";
import DialogBoxWithConfirmation from "../../util/DialogBoxWithConfirmation";

const QuizMarkerForm = ({
  marker,
  markerOpenQuestion,
  setMarkerOpenQuestion,
  markerQcmArray,
  setMarkerQcmArray,
  markerQcmImageArray,
  setMarkerQcmImageArray,
  handleInputFocus,
  handleInputBlur,
}) => {
  const { t, i18n } = useTranslation();
  const OPEN_QUESTION = "openQuestion";
  const QCM = "qcm";
  const QCM_IMAGE = "qcmImage";
  const MAX_FILE_SIZE = 5 * 1024 * 1024;
  const defaultSuccess = t("defaultSuccessMessage");
  const defaultError = t("defaultErrorMessage");
  const defaultNeutral = t("defaultNeutralMessage");
  const completedQuestionForm = (questionForm) => {
    let questionFormCompleted = false;
    if (questionForm === "OpenQuestionMarkerForm") {
      if (markerOpenQuestion.length === 0) {
        questionFormCompleted = true;
      } else {
        const current = markerOpenQuestion[markerOpenQuestion.length - 1];

        const hasValidQuestion = current.question !== DEFAULT_EMPTY_TEXT;
        const hasValidAnswer =
          current.noCorrectAnswer ||
          (current.answer && current.answer.trim() !== DEFAULT_EMPTY_TEXT);

        if (hasValidQuestion && hasValidAnswer) {
          questionFormCompleted = true;
        }
      }
    } else if (questionForm === "QcmMarkerForm") {
      if (markerQcmArray.length === 0) {
        questionFormCompleted = true;
      } else {
        const current = markerQcmArray[markerQcmArray.length - 1];

        const hasValidQuestion =
          current.question !== DEFAULT_EMPTY_TEXT &&
          current.answersArray.every((a) => a !== DEFAULT_EMPTY_TEXT);

        const hasValidAnswer =
          current.noCorrectAnswer ||
          (current.answer && current.answer.trim() !== DEFAULT_EMPTY_TEXT);

        if (hasValidQuestion && hasValidAnswer) {
          questionFormCompleted = true;
        }
      }
    } else if (questionForm === "QcmImageMarkerForm") {
      if (markerQcmImageArray.length === 0) {
        questionFormCompleted = true;
      } else {
        const current = markerQcmImageArray[markerQcmImageArray.length - 1];
        const hasValidQuestion =
          current.question !== DEFAULT_EMPTY_TEXT &&
          current.answersArray.every((a) => a !== DEFAULT_EMPTY_TEXT);

        const hasValidAnswer =
          current.noCorrectAnswer ||
          (current.answer && current.answer.trim() !== DEFAULT_EMPTY_TEXT);

        if (hasValidQuestion && hasValidAnswer) {
          questionFormCompleted = true;
        }
      }
    }

    return questionFormCompleted;
  };

  const handleDeleteQuestion = (index, type) => async (e) => {
    e.stopPropagation();
    const confirmDelete = await DialogBoxWithConfirmation({
      title: t("deleteQuiz"),
      text: t("deleteQuizText"),
      icon: "warning",
      cancelButtonText: t("no"),
      confirmButtonText: t("yes"),
    });
    if (confirmDelete) {
      if (type === OPEN_QUESTION) {
        setMarkerOpenQuestion((prevArray) =>
          prevArray.filter((_, i) => i !== index)
        );
      } else if (type === QCM) {
        setMarkerQcmArray((prevArray) =>
          prevArray.filter((_, i) => i !== index)
        );
      } else if (type === QCM_IMAGE) {
        setMarkerQcmImageArray((prevArray) =>
          prevArray.filter((_, i) => i !== index)
        );
      }
    }
  };

  const handleNeutralMessageChange = (value, index) => {
    const newArray = [...markerQcmArray];
    newArray[index].neutralMessage = value;
    setMarkerQcmArray(newArray);
  };

  const handleNoCorrectAnswerToggle = (index) => {
    const newArray = [...markerQcmArray];
    newArray[index].noCorrectAnswer = !newArray[index].noCorrectAnswer;

    // Si on active "aucune bonne réponse", on vide correctAnswers
    if (newArray[index].noCorrectAnswer) {
      newArray[index].correctAnswers = [];
    }

    setMarkerQcmArray(newArray);
  };

  //////////////////////// Pour les questions ouvertes /////////////////////////////
  const addQuestion = (event) => {
    event.preventDefault();
    if (completedQuestionForm("OpenQuestionMarkerForm")) {
      setMarkerOpenQuestion([
        ...markerOpenQuestion,
        {
          question: DEFAULT_EMPTY_TEXT,
          answer: DEFAULT_EMPTY_TEXT,
          successMessage: defaultSuccess,
          errorMessage: defaultError,
          neutralMessage: defaultNeutral,
          noCorrectAnswer: false,
        },
      ]);
    }
  };

  const handleQuestionChange = (value, index) => {
    const newQuestions = [...markerOpenQuestion];
    newQuestions[index] = {
      ...newQuestions[index],
      question: value,
    };
    setMarkerOpenQuestion(newQuestions);
  };

  const handleAnswerChange = (value, index) => {
    const newAnswers = [...markerOpenQuestion];
    newAnswers[index] = {
      ...newAnswers[index],
      answer: value,
    };
    setMarkerOpenQuestion(newAnswers);
  };

  const handleOpenQuestionNoCorrectAnswerToggle = (index) => {
    const newArray = [...markerOpenQuestion];
    newArray[index].noCorrectAnswer = !newArray[index].noCorrectAnswer;
    setMarkerOpenQuestion(newArray);
  };

  const handleOpenQuestionNeutralMessageChange = (value, index) => {
    const newArray = [...markerOpenQuestion];
    newArray[index].neutralMessage = value;
    setMarkerOpenQuestion(newArray);
  };

  const handleSuccessMessageChange = (value, index) => {
    const newSuccessMessages = [...markerOpenQuestion];
    newSuccessMessages[index] = {
      ...newSuccessMessages[index],
      successMessage: value,
    };
    setMarkerOpenQuestion(newSuccessMessages);
  };

  const handleErrorMessageChange = (value, index) => {
    const newErrorMessages = [...markerOpenQuestion];
    newErrorMessages[index] = {
      ...newErrorMessages[index],
      errorMessage: value,
    };
    setMarkerOpenQuestion(newErrorMessages);
  };

  //////////////////////// Pour le qcm /////////////////////////////
  const handleAddAnswer = (event, questionIndex) => {
    event.preventDefault();

    const newArray = [...markerQcmArray];
    if (newArray[questionIndex].answersArray.length < 13) {
      newArray[questionIndex].answersArray.push(DEFAULT_EMPTY_TEXT);
      setMarkerQcmArray(newArray);
    }
  };

  const addQcmQuestion = (event) => {
    event.preventDefault();
    if (completedQuestionForm("QcmMarkerForm")) {
      setMarkerQcmArray([
        ...markerQcmArray,
        {
          question: DEFAULT_EMPTY_TEXT,
          answersArray: [DEFAULT_EMPTY_TEXT, DEFAULT_EMPTY_TEXT],
          correctAnswers: [],
          successMessage: defaultSuccess,
          errorMessage: defaultError,
          neutralMessage: defaultNeutral,
          noCorrectAnswer: false,
        },
      ]);
    }
  };

  const handleQcmQuestionChange = (value, index) => {
    const newArray = [...markerQcmArray];
    newArray[index].question = value;
    setMarkerQcmArray(newArray);
  };

  const handleQcmAnswerChange = (value, questionIndex, answerIndex) => {
    const newArray = [...markerQcmArray];
    newArray[questionIndex].answersArray[answerIndex] = value;
    setMarkerQcmArray(newArray);
  };

  const handleQcmSuccessMessageChange = (value, index) => {
    const newArray = [...markerQcmArray];
    newArray[index].successMessage = value;
    setMarkerQcmArray(newArray);
  };

  const handleQcmErrorMessageChange = (value, index) => {
    const newArray = [...markerQcmArray];
    newArray[index].errorMessage = value;
    setMarkerQcmArray(newArray);
  };

  const handlecorrectAnswersChange = (event, questionIndex, answerIndex) => {
    const newArray = [...markerQcmArray];
    let correctAnswers = newArray[questionIndex].correctAnswers || [];
    correctAnswers = correctAnswers.map((i) => parseInt(i, 10));

    if (event.target.checked) {
      if (!correctAnswers.includes(answerIndex)) {
        correctAnswers.push(answerIndex);
      }
    } else {
      correctAnswers = correctAnswers.filter((i) => i !== answerIndex);
    }

    newArray[questionIndex].correctAnswers = correctAnswers;

    setMarkerQcmArray(newArray);
  };

  //////////////////////// Pour le qcmImage /////////////////////////////

  const handleAddImageAnswer = (event, questionIndex) => {
    event.preventDefault();

    const newArray = [...markerQcmImageArray];
    if (newArray[questionIndex].answersArray.length < 13) {
      newArray[questionIndex].answersArray.push(DEFAULT_EMPTY_TEXT);
      // Titre de la nouvelle case : sans lui, un titre saisi plus loin
      // laissait un trou (null en JSON) que le mobile ne savait pas lire.
      const titles = newArray[questionIndex].answersTitleArray || [];
      while (titles.length < newArray[questionIndex].answersArray.length) {
        titles.push(DEFAULT_EMPTY_TEXT);
      }
      newArray[questionIndex].answersTitleArray = titles;
      setMarkerQcmImageArray(newArray);
    }
  };

  const handleQcmImageQuestionChange = (value, index) => {
    const newArray = [...markerQcmImageArray];
    newArray[index].question = value;
    setMarkerQcmImageArray(newArray);
  };
  const handleQcmImageNoCorrectAnswerToggle = (index) => {
    const newArray = [...markerQcmImageArray];
    newArray[index].noCorrectAnswer = !newArray[index].noCorrectAnswer;

    if (newArray[index].noCorrectAnswer) {
      newArray[index].correctAnswers = [];
    }

    setMarkerQcmImageArray(newArray);
  };
  const handleQcmImageAnswerChange = async (
    event,
    questionIndex,
    answerIndex
  ) => {
    const file = event.target.files[0];

    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        // Afficher un message d'erreur si la taille du fichier dépasse 5 Mo
        DialogBox({
          text: t("fileSizeError"),
          icon: "error",
          confirmButtonText: "OK",
        });
        return;
      }
      LoadingBox({
        text: t("loadingAdd"),
        icon: "info",
      });

      try {
        // Mise en file locale (type `qcm`) : l'envoi réel attend la
        // sauvegarde du POI (ADR 0005). La clé de contenu est le SHA-256 du
        // fichier, identique au checksum serveur : la référence peut donc
        // être écrite avant l'upload. null = contenu déjà en file (doublon) :
        // on réutilise alors son empreinte.
        let checksum = await queuePoiMediaUpload(marker.id, "qcm", file);
        if (!checksum) {
          checksum = await computePoiMediaContentKey(file);
        }
        if (!isImageChecksum(checksum)) {
          // Empreinte illisible (fichier non lisible) : rien n'est écrit.
          throw new Error("Empreinte du fichier indisponible.");
        }
        // Seule la référence est sauvegardée ; l'aperçu est résolu à
        // l'affichage depuis la file d'envoi (jamais de data URL dans l'état).
        setMarkerQcmImageArray((previous) => {
          // La question (ou la case) a pu être supprimée pendant le calcul
          // de l'empreinte : ne rien écrire plutôt que créer un trou.
          if (
            !previous[questionIndex] ||
            answerIndex >= (previous[questionIndex].answersArray || []).length
          ) {
            return previous;
          }
          const newArray = [...previous];
          const item = { ...newArray[questionIndex] };
          const answers = [...(item.answersArray || [])];
          answers[answerIndex] = qcmReferenceFor(checksum);
          item.answersArray = answers;
          newArray[questionIndex] = item;
          return newArray;
        });
      } catch (error) {
        console.error(
          "Une erreur s'est produite lors de la mise à jour de l'image:",
          error
        );
        closeLoadingBox();
        DialogBox({
          text: t("qcmImageUploadError"),
          icon: "error",
          confirmButtonText: "OK",
        });
        return;
      }
      // Fermer le loading box
      closeLoadingBox();
    }
  };

  const handleQcmImageTitleChange = (value, questionIndex, titleIndex) => {
    const newArray = [...markerQcmImageArray];
    newArray[questionIndex].answersTitleArray[titleIndex] = value;
    setMarkerQcmImageArray(newArray);
  };

  const handleQcmImageSuccessMessageChange = (value, index) => {
    const newArray = [...markerQcmImageArray];
    newArray[index].successMessage = value;
    setMarkerQcmImageArray(newArray);
  };

  const handleQcmImageErrorMessageChange = (value, index) => {
    const newArray = [...markerQcmImageArray];
    newArray[index].errorMessage = value;
    setMarkerQcmImageArray(newArray);
  };

  const handleQcmImagecorrectAnswersChange = (
    event,
    questionIndex,
    answerIndex
  ) => {
    const newArray = [...markerQcmImageArray];
    let correctAnswers = newArray[questionIndex].correctAnswers || [];
    correctAnswers = correctAnswers.map((i) => parseInt(i, 10));

    if (event.target.checked) {
      if (!correctAnswers.includes(answerIndex)) {
        correctAnswers.push(answerIndex);
      }
    } else {
      correctAnswers = correctAnswers.filter((i) => i !== answerIndex);
    }

    newArray[questionIndex].correctAnswers = correctAnswers;

    setMarkerQcmImageArray(newArray);
  };
  const handleQcmImageNeutralMessageChange = (value, index) => {
    const newArray = [...markerQcmImageArray];
    newArray[index].neutralMessage = value;
    setMarkerQcmImageArray(newArray);
  };

  const addQcmImage = (event) => {
    event.preventDefault();
    if (completedQuestionForm("QcmImageMarkerForm")) {
      setMarkerQcmImageArray([
        ...markerQcmImageArray,
        {
          question: DEFAULT_EMPTY_TEXT,
          answersArray: [DEFAULT_EMPTY_TEXT, DEFAULT_EMPTY_TEXT],
          answersTitleArray: [DEFAULT_EMPTY_TEXT, DEFAULT_EMPTY_TEXT],
          correctAnswers: [],
          successMessage: defaultSuccess,
          errorMessage: defaultError,
          neutralMessage: defaultNeutral,
          noCorrectAnswer: false,
        },
      ]);
    }
  };

  return (
    <>
      <button
        className="buttonModifMarker mt-2 w-100 p-2 "
        onClick={(event) => addQuestion(event)}
      >
        {t("addQuestion")}
      </button>
      <button
        className="buttonModifMarker mt-2 w-100 p-2 "
        onClick={(event) => addQcmQuestion(event)}
      >
        {t("addQCM")}
      </button>
      <button
        className="buttonModifMarker mt-2 w-100 p-2 "
        onClick={(event) => addQcmImage(event)}
      >
        {t("addQcmImage")}
      </button>
      {markerOpenQuestion.map((item, index) => (
        <div className="cardOpenQuestion" key={index}>
          <OpenQuestionMarkerForm
            question={item.question}
            answer={item.answer}
            successMessage={item.successMessage}
            errorMessage={item.errorMessage}
            noCorrectAnswer={item.noCorrectAnswer}
            neutralMessage={item.neutralMessage}
            handleNoCorrectAnswerToggle={
              handleOpenQuestionNoCorrectAnswerToggle
            }
            handleNeutralMessageChange={handleOpenQuestionNeutralMessageChange}
            handleInputFocus={handleInputFocus}
            handleInputBlur={handleInputBlur}
            handleQuestionChange={(event) => handleQuestionChange(event, index)}
            handleAnswerChange={(event) => handleAnswerChange(event, index)}
            handleSuccessMessageChange={(event) =>
              handleSuccessMessageChange(event, index)
            }
            handleErrorMessageChange={(event) =>
              handleErrorMessageChange(event, index)
            }
            index={index}
          />
          <button
            type="button"
            className="buttonDeleteQuiz mt-3 mb-3 me-2"
            onClick={handleDeleteQuestion(index, OPEN_QUESTION)}
            aria-label="delete"
          >
            <i className="bi bi-x"></i>
          </button>
        </div>
      ))}
      {markerQcmArray.map((item, index) => (
        <div className="cardQcm" key={index}>
          <QcmMarkerForm
            question={item.question}
            answersArray={item.answersArray}
            correctAnswers={item.correctAnswers}
            successMessage={item.successMessage}
            errorMessage={item.errorMessage}
            neutralMessage={item.neutralMessage}
            handleNeutralMessageChange={handleNeutralMessageChange}
            noCorrectAnswer={item.noCorrectAnswer}
            handleNoCorrectAnswerToggle={handleNoCorrectAnswerToggle}
            handleInputFocus={handleInputFocus}
            handleInputBlur={handleInputBlur}
            handleQuestionChange={(event) =>
              handleQcmQuestionChange(event, index)
            }
            handleAnswerChange={(event, answerIndex) =>
              handleQcmAnswerChange(event, index, answerIndex)
            }
            handleCorrectAnswerChange={(event, answerIndex) =>
              handlecorrectAnswersChange(event, index, answerIndex)
            }
            handleSuccessMessageChange={(event) =>
              handleQcmSuccessMessageChange(event, index)
            }
            handleErrorMessageChange={(event) =>
              handleQcmErrorMessageChange(event, index)
            }
            handleAddAnswer={(event) => handleAddAnswer(event, index)}
            index={index}
          />
          <button
            type="button"
            className="buttonDeleteQuiz mt-3 mb-3 me-2"
            onClick={handleDeleteQuestion(index, QCM)}
            aria-label="delete"
          >
            <i className="bi bi-x"></i>
          </button>
        </div>
      ))}

      {markerQcmImageArray.map((item, index) => (
        <div className="cardQcmImage" key={index}>
          <QcmImageMarkerForm
            marker={marker}
            question={item.question}
            answersArray={item.answersArray}
            answersTitleArray={item.answersTitleArray}
            correctAnswers={item.correctAnswers}
            noCorrectAnswer={item.noCorrectAnswer}
            successMessage={item.successMessage}
            errorMessage={item.errorMessage}
            neutralMessage={item.neutralMessage}
            handleInputFocus={handleInputFocus}
            handleInputBlur={handleInputBlur}
            handleNeutralMessageChange={handleQcmImageNeutralMessageChange}
            handleNoCorrectAnswerToggle={handleQcmImageNoCorrectAnswerToggle}
            handleQuestionChange={(event) =>
              handleQcmImageQuestionChange(event, index)
            }
            handleAnswerChange={(event, answerIndex) =>
              handleQcmImageAnswerChange(event, index, answerIndex)
            }
            handleQcmImageTitleChange={(event, titleIndex) =>
              handleQcmImageTitleChange(event, index, titleIndex)
            }
            handleCorrectAnswerChange={(event, answerIndex) =>
              handleQcmImagecorrectAnswersChange(event, index, answerIndex)
            }
            handleSuccessMessageChange={(event) =>
              handleQcmImageSuccessMessageChange(event, index)
            }
            handleErrorMessageChange={(event) =>
              handleQcmImageErrorMessageChange(event, index)
            }
            handleAddAnswer={(event) => handleAddImageAnswer(event, index)}
            index={index}
          />
          <button
            type="button"
            className="buttonDeleteQuiz mt-3 mb-3 me-2"
            onClick={handleDeleteQuestion(index, QCM_IMAGE)}
            aria-label="delete"
          >
            <i className="bi bi-x"></i>
          </button>
        </div>
      ))}
    </>
  );
};

export default QuizMarkerForm;
